import React, { useContext, useEffect, useState } from "react";
import Modal from "react-modal";
import { Context } from "../contexts/AppContext";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowDown,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";

function DownloadVideoModal() {
  const { value } = useContext(Context);
  const [appState, setAppState] = value;

  const [modalOpen, setModalOpen] = useState(appState.modalData);

  const [downloadProgress, setDownloadProgress] = useState({});
  const [downloading, setDownloading] = useState({});
  const [completed, setCompleted] = useState({});
  const [downloadingAll, setDownloadingAll] = useState(false);
const [allProgress, setAllProgress] = useState(0);

  useEffect(() => {
    setModalOpen(appState.modalData);
  }, [appState.modalData]);

  Modal.setAppElement("#root");

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      transform: "translate(-50%, -50%)",
      width: "100%",
      maxWidth: "650px",
      maxHeight: "80vh",
      borderRadius: "16px",
      border: "none",
      padding: "20px",
      background: "#f8fafc",
      overflow: "hidden",
    },
    overlay: {
      backgroundColor: "rgba(0,0,0,0.45)",
      zIndex: 9999,
    },
  };


  const downloadAllVideos = async () => {
  if (!appState?.videoArr?.length) return;

  setDownloadingAll(true);
  setAllProgress(0);

  try {
    const totalVideos = appState.videoArr.length;

    for (let i = 0; i < totalVideos; i++) {
      const item = appState.videoArr[i];

      await handleDownload(
        `${process.env.REACT_APP_VIDEO_DEST_BASE_PATH}${item.url}`,
        `${item.title}.mp4`
      );

      setAllProgress(
        Math.round(((i + 1) / totalVideos) * 100)
      );

      await new Promise((resolve) =>
        setTimeout(resolve, 300)
      );
    }
  } catch (err) {
    console.error(err);
  } finally {
    setDownloadingAll(false);
  }
};

  const handleDownload = async (url, filename) => {
    try {
      setDownloading((prev) => ({
        ...prev,
        [filename]: true,
      }));

      setCompleted((prev) => ({
        ...prev,
        [filename]: false,
      }));

      setDownloadProgress((prev) => ({
        ...prev,
        [filename]: 0,
      }));

      const response = await fetch(url, {
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const contentLength =
        response.headers.get("content-length");

      const total = contentLength
        ? parseInt(contentLength, 10)
        : 0;

      const reader = response.body.getReader();

      let receivedLength = 0;
      const chunks = [];

      while (true) {
        const { done, value } =
          await reader.read();

        if (done) break;

        chunks.push(value);
        receivedLength += value.length;

        if (total > 0) {
          const progress = Math.round(
            (receivedLength / total) * 100
          );

          setDownloadProgress((prev) => ({
            ...prev,
            [filename]: progress,
          }));
        }
      }

      const blob = new Blob(chunks);

      const blobUrl =
        window.URL.createObjectURL(blob);

      const a = document.createElement("a");

      a.href = blobUrl;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);

      setDownloadProgress((prev) => ({
        ...prev,
        [filename]: 100,
      }));

      setCompleted((prev) => ({
        ...prev,
        [filename]: true,
      }));
    } catch (error) {
      console.error("Download failed:", error);
      alert("Download failed");
    } finally {
      setDownloading((prev) => ({
        ...prev,
        [filename]: false,
      }));
    }
  };

  const closeModal = () => {
    setAppState((prevState) => ({
      ...prevState,
      modalData: {
        ...prevState.modalData,
        type: null,
        isOpen: false,
      },
    }));
  };

  return (
    <Modal
      isOpen={modalOpen?.isOpen}
      onRequestClose={closeModal}
      style={customStyles}
      contentLabel="Download Videos"
    >
      {/* Header */}
<div className="flex items-center justify-between border-b pb-4 mb-4">
  <h2 className="text-2xl font-bold text-gray-800">
    Download Videos
  </h2>

  <div className="flex items-center gap-3">
    <button
      onClick={downloadAllVideos}
      disabled={downloadingAll}
      className={`px-4 py-2 rounded-lg text-white font-medium transition
      ${
        downloadingAll
          ? "bg-gray-400 cursor-not-allowed"
          : "bg-blue-600 hover:bg-blue-700"
      }`}
    >
      {downloadingAll
        ? `Downloading ${allProgress}%`
        : "Download All"}
    </button>

    <button
      onClick={closeModal}
      className="text-gray-500 hover:text-red-500 text-2xl"
    >
      ✕
    </button>
  </div>
</div>
      
      {downloadingAll && (
  <div className="mb-4">
    <div className="flex justify-between text-sm mb-1">
      <span>Downloading All Videos</span>
      <span>{allProgress}%</span>
    </div>

    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
      <div
        className="bg-blue-600 h-3 transition-all duration-300"
        style={{
          width: `${allProgress}%`,
        }}
      />
    </div>
  </div>
)}
      {/* Video List */}
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
        {appState?.videoArr?.map((item, index) => {
          const filename = `${item.title}.mp4`;

          const progress =
            downloadProgress[filename] || 0;

          const isDownloading =
            downloading[filename];

          const isCompleted =
            completed[filename];

          return (
            <div
              key={index}
              className="bg-white border rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 break-words">
                    {item.title}
                  </h3>

                  {(isDownloading ||
                    progress > 0) && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>
                          {isCompleted
                            ? "Completed"
                            : "Downloading"}
                        </span>

                        <span>
                          {progress}%
                        </span>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-green-500 h-2 transition-all duration-300"
                          style={{
                            width: `${progress}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <button
                  disabled={isDownloading}
                  onClick={() =>
                    handleDownload(
                      `${process.env.REACT_APP_VIDEO_DEST_BASE_PATH}${item.url}`,
                      filename
                    )
                  }
                  className={`min-w-[140px] flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-all
                  ${
                    isCompleted
                      ? "bg-green-600"
                      : isDownloading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gray-800 hover:bg-black"
                  }`}
                >
                  {isCompleted ? (
                    <>
                      Done
                      <FontAwesomeIcon
                        icon={faCircleCheck}
                      />
                    </>
                  ) : isDownloading ? (
                    `${progress}%`
                  ) : (
                    <>
                      Download
                      <FontAwesomeIcon
                        icon={faCloudArrowDown}
                      />
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

export default DownloadVideoModal;